<template>
  <div>
    <v-alert
        v-if="error"
        type="error"
        dense
        text
    >
      {{ error }}
    </v-alert>

    <v-select
        v-model="nemsisVersion"
        :items="supportedVersions"
        label="NEMSIS version"
        dense
        hide-details
        style="max-width: 160px"
        class="mb-2"
    />

    <p
        v-if="!loading && !assignments.length"
        class="grey--text text--darken-1 mb-0"
    >
      {{ $t('schematrons.clientPanel.empty') }}
    </p>

    <v-progress-linear
        v-if="loading"
        indeterminate
    />

    <!-- Pulled out by the content padding of the panel so the table spans its full width.
         Cells carry the same 24px, which lands the column titles exactly under the panel
         header instead of indented inside it. The explicit width is needed because
         v-simple-table is width:100%, so negative margins alone move it without
         stretching it. -->
    <v-simple-table
        v-if="assignments.length"
        dense
        class="mx-n6 schematrons-table"
    >
      <thead>
        <tr>
          <th>Schematron</th>
          <th style="width: 110px">Scope</th>
          <th style="width: 110px">Source</th>
          <th style="width: 130px">Status</th>
          <th style="width: 90px"></th>
        </tr>
      </thead>
      <tbody>
        <tr
            v-for="assignment in assignments"
            :key="assignment.schematron.id"
        >
          <td>
            {{ assignment.schematron.name }}
            <p class="mb-0">
              <small class="grey--text text--darken-1">
                {{ assignment.schematron.datasetType }} {{ assignment.schematron.nemsisVersion }}
              </small>
            </p>
          </td>
          <td>{{ assignment.schematron.scope }}</td>
          <td>
            {{ assignment.source === 'auto'
              ? $t('schematrons.clientPanel.auto')
              : $t('schematrons.clientPanel.manual') }}
          </td>
          <td>
            <v-chip
                :color="statusColor(assignment)"
                x-small
                dark
            >
              {{ statusLabel(assignment) }}
            </v-chip>
          </td>
          <td class="text-end">
            <v-switch
                :input-value="assignment.isApplied"
                :disabled="saving"
                dense
                hide-details
                class="mt-0 pt-0"
                @change="toggle(assignment, $event)"
            />
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <p class="caption grey--text text--darken-1 mt-3 mb-0">
      National rulesets and the ones belonging to this client's state apply on their own.
      Turning one off records an exclusion for this client only; county rulesets have to be
      turned on by hand.
    </p>
  </div>
</template>
<script>
import schematronsApi from '@/api/schematrons';

const SOURCE_AUTO = 'auto';

export default {
  name: 'SchematronsPanel',

  props: {
    clientId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      assignments: [],
      supportedVersions: [],
      // Defaulted to the version being submitted today; the next one is selectable because
      // its rulesets are attached before the switch.
      nemsisVersion: null,
    };
  },

  watch: {
    clientId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    nemsisVersion() {
      this.refresh();
    },
  },

  methods: {
    async load() {
      if (!this.supportedVersions.length) {
        const meta = await schematronsApi.getMeta();
        this.supportedVersions = meta.supportedVersions;
        // Assigning it triggers the watcher, which loads the assignments.
        this.nemsisVersion = meta.version;
        return;
      }

      await this.refresh();
    },

    async refresh() {
      this.assignments = [];
      if (!this.clientId) {
        return;
      }

      this.loading = true;
      this.error = null;
      try {
        const response = await schematronsApi.getClientSchematrons(
          this.clientId,
          this.nemsisVersion,
        );
        this.assignments = response.data;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message)
          || 'Could not load schematrons for this client.';
      } finally {
        this.loading = false;
      }
    },

    statusLabel(assignment) {
      if (assignment.isApplied) {
        return this.$t('schematrons.clientPanel.applied');
      }
      return assignment.source === SOURCE_AUTO
        ? this.$t('schematrons.clientPanel.excluded')
        : this.$t('schematrons.clientPanel.available');
    },

    statusColor(assignment) {
      if (assignment.isApplied) {
        return 'success';
      }
      return assignment.source === SOURCE_AUTO ? 'error' : 'grey';
    },

    /**
     * A row is only worth storing when the decision differs from what the ruleset does by
     * itself: an exclusion for an automatic one, an attachment for a manual one. Agreeing
     * with the default drops the row instead, so the client keeps following it rather than
     * being pinned to a decision that merely happens to match today.
     */
    async toggle(assignment, isEnabled) {
      this.saving = true;
      this.error = null;
      try {
        const schematronId = assignment.schematron.id;
        const appliesByDefault = assignment.source === SOURCE_AUTO;

        if (isEnabled === appliesByDefault) {
          await schematronsApi.resetClientSchematron(this.clientId, schematronId);
        } else {
          await schematronsApi.setClientSchematron(this.clientId, schematronId, isEnabled);
        }

        await this.refresh();
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message)
          || 'Could not update the schematron assignment.';
        await this.refresh();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style scoped>
/* Panel content padding is 24px a side; the table is pulled over both of them so it spans
   the panel edge to edge. max-width has to go with it - Vuetify caps v-data-table at 100%
   of the content box, which clamps the width back and leaves a gap on the right. The
   v-data-table part of the selector is for specificity. */
.v-data-table.schematrons-table {
  width: calc(100% + 48px);
  max-width: none;
}
</style>
